import { DeductionRequestStatus } from '@aslanpay/portal-common-lib';

import { createColumnHelper, HeaderContext } from '@tanstack/react-table';

import { Badge, Text, Tooltip } from '@chakra-ui/react';

import { format, parseISO } from 'date-fns';

import { DeductionRequestsColumns } from 'app/constants/table';
import { DeductionRequestTableRow } from 'app/components/Individuals/IndividualProfile/IndividualDeductionRequestsTab/types';
import { formatCurrency, toHumanReadable } from 'app/utils/strings';
import DeductionRequestsCategoryValues from 'app/constants/deductionRequests';
import { RepeatIcon } from '@chakra-ui/icons';

// Columns the wallet-manager list endpoint accepts as sortBy. Sorting any other
// column would send an unsupported sortBy, so we only enable it for these.
const SORTABLE_COLUMNS = new Set<keyof DeductionRequestTableRow>([
  'status',
  'createdAt',
  'updatedAt',
  'id',
]);

const RIGHT_ALIGNED_COLUMNS = ['amount', 'recurringAmount'];

const formatDate = (value?: string | null) =>
  value ? format(parseISO(value), 'dd MMM yyyy') : '-';

const formatDateTime = (value?: string | null) =>
  value ? format(parseISO(value), 'dd MMM yyyy, HH:mm') : '-';

const toNumber = (value: unknown): number => {
  if (typeof value === 'string') {
    return parseInt(value, 10);
  }
  if (typeof value === 'number') {
    return value;
  }
  return 0;
};

const renderTruncatedId = (value?: string | null) => {
  if (!value) {
    return <Text>-</Text>;
  }
  return (
    <Tooltip hasArrow shouldWrapChildren placement="top" label={value}>
      <Text>{`${value.slice(0, 8)}…`}</Text>
    </Tooltip>
  );
};

function getDeductionRequestsColumns() {
  const columnHelper = createColumnHelper<DeductionRequestTableRow>();

  const columns = Object.keys(DeductionRequestsColumns).map((field) => {
    return columnHelper.accessor(field as keyof DeductionRequestTableRow, {
      cell: ({ row, getValue }) => {
        const value = getValue();
        const currency = row.original.currency || 'GBP';

        switch (field as keyof DeductionRequestTableRow) {
          case 'status':
            return (
              <Badge
                colorScheme={`badge.${
                  DeductionRequestsCategoryValues[value as DeductionRequestStatus]?.color
                }`}
              >
                {DeductionRequestsCategoryValues[value as DeductionRequestStatus]?.value}
              </Badge>
            );
          case 'payCycleId':
            return <Text>{row.original.period}</Text>;
          case 'reason':
            return <Text>{value ? toHumanReadable(value as string) : '-'}</Text>;
          case 'amount':
            return (
              <Text textAlign="right">{formatCurrency(toNumber(value), currency, 'en-GB')}</Text>
            );
          case 'recurringAmount':
            return (
              <Text textAlign="right">
                {row.original.isRecurring && value != null
                  ? formatCurrency(toNumber(value), currency, 'en-GB')
                  : '-'}
              </Text>
            );
          case 'completeOnDate':
            return <Text>{formatDate(value as string)}</Text>;
          case 'completeNumDaysBeforePayday':
            return <Text>{value != null ? String(value) : '-'}</Text>;
          case 'notes':
            return <Text>{(value as string) || '-'}</Text>;
          case 'createdAt':
          case 'updatedAt':
          case 'cancelledAt':
            return <Text>{formatDateTime(value as string)}</Text>;
          case 'id':
          case 'transactionId':
            return renderTruncatedId(value as string | null);
          case 'isRecurring':
            if (row.original.isRecurring) {
              return (
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  arrowPadding={0}
                  placement="top"
                  label="Recurring"
                >
                  <RepeatIcon color="brand.purple" boxSize={4} />
                </Tooltip>
              );
            }
            return null;
          default:
            return value;
        }
      },
      header: DeductionRequestsColumns[field as keyof typeof DeductionRequestsColumns],
      enableSorting: SORTABLE_COLUMNS.has(field as keyof DeductionRequestTableRow),
      meta: {
        getHeaderContext: (context: HeaderContext<DeductionRequestTableRow, unknown>) => {
          if (RIGHT_ALIGNED_COLUMNS.includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
      },
    });
  });

  return columns;
}

export default getDeductionRequestsColumns;
